import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Card, Button, Form, Row, Col } from 'react-bootstrap';
import { RiSearchLine } from 'react-icons/ri';
import Breadcrumbs from '../../Common/Breadcrumbs';
import PlayerTable from '../../Common/PlayerTable';
import { DeleteModal } from '../../Common/DeleteModal';
import Select from 'react-select';
import ShimmerLoader from '../../Common/ShimmerLoader';
import {
  getAllPlayers,
  ManagePlayer,
} from '../../api/club/managePlayersService';
import { deletePlayer } from '../../api/club/playerService';
import {
  hasAnyPermission,
  hasTournamentWriteAccess,
} from '../../utils/roleBasedAccess';
import './ManagePlayers.css';
import { getImageUrl } from '../../utils/imageUrl';
import PlayerAssetPreviewModal, { PlayerAssetType } from '../../components/PlayerAssetPreviewModal';
import { PLAYER_ASSETS_ENABLED } from '../../constants/featureFlags';
import { downloadFileFromUrl } from '../../utils/fileDownload';

interface Player {
  _id: string;
  playerId?: string;
  fullName: string;
  team: {
    name: string;
    shortName: string;
    logo: string;
  };
  dob: string;
  sports: string;
  verifyStat: 'Approved' | 'Referred Back' | 'Pending';
  paymentStat: 'Paid' | 'Not Paid' | 'Pending';
  amountPaid: number;
  amountDue: number;
  playerImage: string;
  ageCategory: string;
  isSelected?: boolean;
  qrCodeUrl?: string;
  playerIdCardUrl?: string;
}

const ManagePlayers = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  
  // Page size constant - requested from backend
  const REQUESTED_PAGE_SIZE = 200;
  
  // State management
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageCategoryFilter, setAgeCategoryFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playerToDelete, setPlayerToDelete] = useState<Player | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [assetPreviewConfig, setAssetPreviewConfig] = useState<{ player: Player; type: PlayerAssetType } | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [actualPageSize, setActualPageSize] = useState<number>(10); // Track actual page size from backend
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [allPlayersLoaded, setAllPlayersLoaded] = useState(false);
  
  // Dynamic filter options
  const [dynamicAgeCategoryOptions, setDynamicAgeCategoryOptions] = useState<Array<{value: string, label: string}>>([]);
  const [dynamicTeamOptions, setDynamicTeamOptions] = useState<Array<{value: string, label: string}>>([]);

  // Check if user has access
  const userData = {
    ...user,
    subRole: role,
    permissions: permissions,
  };

  const canViewManagePlayers = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.view',
    'tournament.read',
    'tournament.registrations.view',
    'tournament.registrations.manage',
    'tournament.players.view',
    'tournament.players.manage',
  ]);


  useEffect(() => {
    if (!canViewManagePlayers) {
      setLoading(false);
      return;
    }
    loadData();
  }, [canViewManagePlayers]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Automatically load all players for better frontend filtering
      await loadAllPlayersAcrossPages();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert API player to component Player format
  const convertApiPlayerToPlayer = (apiPlayer: ManagePlayer): Player => {
    // Get payment amounts with fallbacks to 0
    const amountPaid = apiPlayer.amountPaid ?? 0;
    const amountDue = apiPlayer.amountDue ?? 0;
    
    // Determine payment status based on amountPaid and amountDue
    let paymentStat: 'Paid' | 'Not Paid' | 'Pending' = 'Not Paid';
    if (amountPaid > 0 && amountDue === 0) {
      paymentStat = 'Paid';
    } else if (amountPaid > 0 && amountDue > 0) {
      paymentStat = 'Pending';
    }
    
    return {
      _id: apiPlayer._id,
      playerId: apiPlayer.playerId,
      fullName: apiPlayer.name,
      team: {
        name: apiPlayer.teamInfo?.name || 'Unknown Team',
        shortName: apiPlayer.teamInfo?.name?.substring(0, 3).toUpperCase() || 'UT',
        logo: apiPlayer.teamInfo?.logo || ''
      },
      dob: apiPlayer.dob,
      sports: 'Football', // Default sport - you might want to get this from player data if available
      verifyStat: (apiPlayer.verificationStatus === 'approved' ? 'Approved' : 
                 apiPlayer.verificationStatus === 'referred_back' ? 'Referred Back' : 'Pending') as 'Approved' | 'Referred Back' | 'Pending',
      paymentStat: paymentStat,
      amountPaid: amountPaid,
      amountDue: amountDue,
      playerImage: apiPlayer.profilePic ? getImageUrl(apiPlayer.profilePic) : '',
      ageCategory: apiPlayer.ageCategory,
      qrCodeUrl: apiPlayer.qrCode ? getImageUrl(apiPlayer.qrCode) : '',
      playerIdCardUrl: apiPlayer.playerIdCard ? getImageUrl(apiPlayer.playerIdCard) : ''
    };
  };

  const loadAllPlayers = async (page: number = 1) => {
    try {
      setIsPageLoading(true);
      setAllPlayersLoaded(false);
      
      const result = await getAllPlayers(page, REQUESTED_PAGE_SIZE);
      
      // Check both possible response structures
      const playersData = result.data?.paginatedResults || (result as any).paginatedResults;
      const totalCount = result.data?.totalCount || (result as any).totalCount;
      
      console.log('Players data:', playersData);
      console.log('Total count:', totalCount);
      console.log('Records returned:', playersData?.length);
      
      if (result && playersData && playersData.length > 0) {
        console.log('Found players in API response:', playersData.length);
        
        // Detect actual page size from backend response
        const detectedPageSize = playersData.length;
        setActualPageSize(detectedPageSize);
        
        // Convert API players to legacy Player format for compatibility
        const convertedPlayers: Player[] = playersData.map((apiPlayer: ManagePlayer) => 
          convertApiPlayerToPlayer(apiPlayer)
        );
        
        setPlayers(convertedPlayers);
        setTotalCount(totalCount);
        // Calculate total pages based on ACTUAL page size returned by backend
        const calculatedTotalPages = Math.ceil(totalCount / detectedPageSize);
        setTotalPages(calculatedTotalPages);
        
        console.log(`Backend returned ${detectedPageSize} records per page (requested ${REQUESTED_PAGE_SIZE})`);
        console.log(`Total pages calculated: ${calculatedTotalPages} (based on ${detectedPageSize} per page)`);
        
        // Extract unique age categories and teams for filters from real data
        const uniqueAgeCategories = Array.from(new Set(playersData.map((p: ManagePlayer) => p.ageCategory)));
        const uniqueTeams = Array.from(new Set(playersData.map((p: ManagePlayer) => p.teamInfo?.name).filter(Boolean)));
        
        setDynamicAgeCategoryOptions([
          { value: 'all', label: 'All' },
          ...uniqueAgeCategories.map(cat => ({ value: cat, label: cat }))
        ]);
        
        setDynamicTeamOptions([
          { value: 'all', label: 'All' },
          ...uniqueTeams.map(team => ({ value: team || '', label: team || '' }))
        ]);
        
      } else {
        console.log('No players found in API response or empty array');
        console.log('Result structure:', result);
        setPlayers([]);
        setTotalCount(0);
        setTotalPages(0);
        setActualPageSize(10); // Default to 10 if no data
      }
      
    } catch (error) {
      console.error('Error in loadAllPlayers:', error);
      setPlayers([]);
      setTotalCount(0);
      setTotalPages(0);
      setActualPageSize(10); // Default to 10 on error
    } finally {
      setIsPageLoading(false);
    }
  };

  // Function to load all players by fetching all pages
  const loadAllPlayersAcrossPages = async () => {
    try {
      setIsLoadingAll(true);
      setAllPlayersLoaded(false);
      
      // First, get the first page to know total count and page size
      const firstPageResult = await getAllPlayers(1, REQUESTED_PAGE_SIZE);
      const firstPageData = firstPageResult.data?.paginatedResults || (firstPageResult as any).paginatedResults;
      const totalCount = firstPageResult.data?.totalCount || (firstPageResult as any).totalCount;
      const detectedPageSize = firstPageData?.length || 10;
      
      console.log(`Loading all ${totalCount} players across pages (${detectedPageSize} per page)`);
      
      // Convert first page
      let allConvertedPlayers: Player[] = firstPageData.map((apiPlayer: ManagePlayer) => 
        convertApiPlayerToPlayer(apiPlayer)
      );
      
      // Calculate total pages
      const totalPages = Math.ceil(totalCount / detectedPageSize);
      
      // Fetch remaining pages in parallel (with reasonable concurrency limit)
      const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
      const batchSize = 5; // Fetch 5 pages at a time to avoid overwhelming the server
      
      for (let i = 0; i < remainingPages.length; i += batchSize) {
        const batch = remainingPages.slice(i, i + batchSize);
        const batchPromises = batch.map(pageNum => getAllPlayers(pageNum, REQUESTED_PAGE_SIZE));
        const batchResults = await Promise.all(batchPromises);
        
        batchResults.forEach((result) => {
          const playersData = result.data?.paginatedResults || (result as any).paginatedResults;
          if (playersData && playersData.length > 0) {
            const converted = playersData.map((apiPlayer: ManagePlayer) => 
              convertApiPlayerToPlayer(apiPlayer)
            );
            allConvertedPlayers = [...allConvertedPlayers, ...converted];
          }
        });
        
        // Update UI progressively
        setPlayers([...allConvertedPlayers]);
        console.log(`Loaded ${allConvertedPlayers.length} of ${totalCount} players...`);
      }
      
      // Extract unique age categories and teams from all players
      const uniqueAgeCategories = Array.from(new Set(allConvertedPlayers.map(p => p.ageCategory)));
      const uniqueTeams = Array.from(new Set(allConvertedPlayers.map(p => p.team.name).filter(Boolean)));
      
      setDynamicAgeCategoryOptions([
        { value: 'all', label: 'All' },
        ...uniqueAgeCategories.map(cat => ({ value: cat, label: cat }))
      ]);
      
      setDynamicTeamOptions([
        { value: 'all', label: 'All' },
        ...uniqueTeams.map(team => ({ value: team || '', label: team || '' }))
      ]);
      
      setPlayers(allConvertedPlayers);
      setTotalCount(totalCount);
      setTotalPages(1); // Show as single page since all loaded
      setAllPlayersLoaded(true);
      
      console.log(`Successfully loaded all ${allConvertedPlayers.length} players`);
      
    } catch (error) {
      console.error('Error loading all players:', error);
      setAllPlayersLoaded(false);
    } finally {
      setIsLoadingAll(false);
    }
  };

  // Age category options - use dynamic data if available, otherwise fallback to static
  const ageCategoryOptions = dynamicAgeCategoryOptions.length > 0 ? dynamicAgeCategoryOptions : [
    { value: 'all', label: 'All' },
    { value: 'U7', label: 'U7' },
    { value: 'U10', label: 'U10' },
    { value: 'U11', label: 'U11' },
    { value: 'U12', label: 'U12' },
    { value: 'U13', label: 'U13' },
    { value: 'U14', label: 'U14' },
    { value: 'U15', label: 'U15' },
    { value: 'U16', label: 'U16' },
    { value: 'U17', label: 'U17' },
    { value: 'U18', label: 'U18' },
    { value: 'Sub Junior Boys', label: 'Sub Junior Boys' }
  ];

  // Team options - use dynamic data if available, otherwise fallback to static
  const teamOptions = dynamicTeamOptions.length > 0 ? dynamicTeamOptions : [
    { value: 'all', label: 'All' },
    { value: 'OFC', label: 'Oranje FC' },
    { value: 'PFC', label: 'Pneuma FC' },
    { value: 'CFC', label: 'Community FC' }
  ];

  // Status options
  const statusOptions = [
    { value: 'all', label: 'All' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Referred Back', label: 'Referred Back' },
    { value: 'Pending', label: 'Pending' }
  ];

  // Filter players based on search and filters
  const filteredPlayers = players.filter(player => {
    const matchesSearch = player.fullName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAgeCategory = ageCategoryFilter === 'all' || player.ageCategory === ageCategoryFilter;
    const matchesTeam = teamFilter === 'all' || player.team.name === teamFilter;
    const matchesStatus = statusFilter === 'all' || player.verifyStat === statusFilter;
    
    return matchesSearch && matchesAgeCategory && matchesTeam && matchesStatus;
  });

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadAllPlayers(page);
  };

  // Handle search
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handle filter changes
  const handleAgeCategoryFilterChange = (selectedOption: any) => {
    setAgeCategoryFilter(selectedOption.value);
  };

  const handleTeamFilterChange = (selectedOption: any) => {
    setTeamFilter(selectedOption.value);
  };

  const handleStatusFilterChange = (selectedOption: any) => {
    setStatusFilter(selectedOption.value);
  };

  // Handle player actions
  const handleViewPlayer = (player: Player) => {
    navigate(`/manage-players/${player._id}`);
  };

  const handleEditPlayer = (player: Player) => {
    navigate(`/manage-players/${player._id}?edit=true`);
  };

  const handleDeletePlayer = (player: Player) => {
    setPlayerToDelete(player);
    setShowDeleteModal(true);
  };

  const handleAssetView = (player: Player, assetType: PlayerAssetType) => {
    setAssetPreviewConfig({ player, type: assetType });
  };

  const handleAssetDownload = async (player: Player, assetType: PlayerAssetType) => {
    const assetUrl = assetType === 'qrCode' ? player.qrCodeUrl : player.playerIdCardUrl;
    if (!assetUrl) return;

    const fallbackName = player.fullName?.replace(/\s+/g, '-').toLowerCase();
    const playerIdentifier = player.playerId || fallbackName || 'player';

    // For QR code, use PNG extension
    if (assetType === 'qrCode') {
      const filename = `${playerIdentifier}-qr-code.png`;
      downloadFileFromUrl(assetUrl, filename);
      return;
    }

    // For player ID card, detect file format from URL or fetch headers
    try {
      let filename: string;
      let urlExtension: string | null = null;
      
      // First, try to extract extension from URL
      try {
        // Handle both absolute and relative URLs
        let urlPath: string;
        if (assetUrl.startsWith('http://') || assetUrl.startsWith('https://')) {
          urlPath = new URL(assetUrl).pathname;
        } else {
          // Relative URL - extract pathname manually
          urlPath = assetUrl.split('?')[0]; // Remove query params
        }
        
        const lastDotIndex = urlPath.lastIndexOf('.');
        if (lastDotIndex > 0 && lastDotIndex < urlPath.length - 1) {
          urlExtension = urlPath.substring(lastDotIndex);
        }
      } catch (urlError) {
        // URL parsing failed, will try Content-Type header
      }
      
      // Check if URL has a valid extension
      const validExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp'];
      const hasValidExtension = urlExtension && validExtensions.some(ext => urlExtension!.toLowerCase().endsWith(ext));
      
      if (hasValidExtension) {
        // Use extension from URL
        filename = `${playerIdentifier}-id-card${urlExtension}`;
      } else {
        // Try to detect from Content-Type header
        try {
          const response = await fetch(assetUrl, { method: 'HEAD' });
          const contentType = response.headers.get('content-type');
          
          let extension = '.pdf'; // Default fallback
          if (contentType) {
            if (contentType.includes('image/png')) extension = '.png';
            else if (contentType.includes('image/jpeg') || contentType.includes('image/jpg')) extension = '.jpg';
            else if (contentType.includes('image/gif')) extension = '.gif';
            else if (contentType.includes('image/webp')) extension = '.webp';
            else if (contentType.includes('application/pdf')) extension = '.pdf';
          }
          
          filename = `${playerIdentifier}-id-card${extension}`;
        } catch (headerError) {
          // If HEAD request fails, default to PDF
          filename = `${playerIdentifier}-id-card.pdf`;
        }
      }
      
      downloadFileFromUrl(assetUrl, filename);
    } catch (error) {
      // Fallback to PDF if anything fails
      const filename = `${playerIdentifier}-id-card.pdf`;
      downloadFileFromUrl(assetUrl, filename);
    }
  };

  const confirmDelete = async () => {
    if (!playerToDelete) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      console.log('Deleting player:', playerToDelete._id);
      
      // Call the delete API (now uses SecureApi and throws on error)
      await deletePlayer(playerToDelete._id);
      
      // Success - close the modal and reload data
      setShowDeleteModal(false);
      setPlayerToDelete(null);
      
      // Reload all players to show updated data
      await loadAllPlayersAcrossPages();
    } catch (error: any) {
      console.error('Error deleting player:', error);
      // Display the specific error message from the API
      setDeleteError(error.message || 'Failed to delete player. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setPlayerToDelete(null);
    setDeleteError(null);
  };

  // Determine write access for players (edit/delete) separately
  const canManagePlayers = hasTournamentWriteAccess(userData, [
    'tournament.players.manage',
  ]);

  // Check access
  if (!canViewManagePlayers) {
    return (
      <Container fluid>
        <div className="text-center py-5">
          <h4>Access Denied</h4>
          <p>Only super-admin and operations-admin users can access this module.</p>
        </div>
      </Container>
    );
  }

  return (
    <div className="all-clubs-page">
      <Container fluid>
        {/* Breadcrumbs - Match All Clubs layout exactly */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs 
            items={[
              { label: 'Dashboard', onClick: () => navigate('/dashboard') },
              { label: 'Player Listing', active: true }
            ]}
          />
        </div>

        {/* Players Header - Using consistent component structure */}
        <div style={{ padding: '0 15px' }}>
          <div className="teams-header-section mb-4" style={{ position: 'relative', zIndex: 10, padding: '0', background: 'transparent', margin: '0', backgroundColor: 'transparent' }}>
            {/* Single Row: Title/Description on Left, Filters on Right */}
            <div className="d-flex justify-content-between align-items-center" style={{ position: 'relative', zIndex: 10, width: '100%', padding: '0', background: 'transparent', backgroundColor: 'transparent' }}>
              {/* Left: Title and Description */}
              <div className="teams-section-info" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: '0 0 auto', background: 'transparent', backgroundColor: 'transparent' }}>
                <h4 className="section-title mb-2" style={{ fontSize: '20px', fontWeight: '600', color: '#495057', margin: '0 0 8px 0', lineHeight: '1.3' }}>Player Listing</h4>
                <p className="section-description mb-0" style={{ fontSize: '14px', color: '#6c757d', margin: '0', lineHeight: '1.4' }}>Manage and view all players in the system</p>
              </div>
              
              {/* Right: Filters */}
              <div className="filter-controls-right"
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px', 
                  justifyContent: 'flex-end', 
                  marginTop: '0',
                  flexWrap: 'nowrap'
                }}
              >
                <div className="filter-group">
                  <label className="filter-label" style={{ 
                    fontSize: '12px', 
                    fontWeight: '500', 
                    color: '#495057', 
                    marginBottom: '4px',
                    display: 'block'
                  }}>
                    Search
                  </label>
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      className="form-search"
                      placeholder="Search Players"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ width: '200px', paddingRight: '35px' }}
                    />
                    {searchTerm && (
                      <button
                        type="button"
                        className="clear-search-btn"
                        onClick={() => setSearchTerm('')}
                        title="Clear search"
                      >
                        <i className="ri-close-line"></i>
                      </button>
                    )}
                  </div>
                </div>
              
              <div className="filter-group">
                <label className="filter-label" style={{ 
                  fontSize: '12px', 
                  fontWeight: '500', 
                  color: '#495057', 
                  marginBottom: '4px',
                  display: 'block'
                }}>
                  Age Category
                </label>
                <Select
                  value={ageCategoryOptions.find(option => option.value === ageCategoryFilter)}
                  onChange={handleAgeCategoryFilterChange}
                  options={ageCategoryOptions}
                  placeholder="Age Category"
                  className="filter-select"
                  classNamePrefix="select"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minWidth: '150px',
                      height: '36px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: 'none'
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: '36px',
                      padding: '0 8px'
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: '0px'
                    }),
                    indicatorSeparator: () => ({
                      display: 'none'
                    }),
                    indicatorsContainer: (provided) => ({
                      ...provided,
                      height: '36px'
                    })
                  }}
                />
              </div>
              
              <div className="filter-group">
                <label className="filter-label" style={{ 
                  fontSize: '12px', 
                  fontWeight: '500', 
                  color: '#495057', 
                  marginBottom: '4px',
                  display: 'block'
                }}>
                  Team
                </label>
                <Select
                  value={teamOptions.find(option => option.value === teamFilter)}
                  onChange={handleTeamFilterChange}
                  options={teamOptions}
                  placeholder="Team"
                  className="filter-select"
                  classNamePrefix="select"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minWidth: '150px',
                      height: '36px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: 'none'
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: '36px',
                      padding: '0 8px'
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: '0px'
                    }),
                    indicatorSeparator: () => ({
                      display: 'none'
                    }),
                    indicatorsContainer: (provided) => ({
                      ...provided,
                      height: '36px'
                    })
                  }}
                />
              </div>
              
              <div className="filter-group">
                <label className="filter-label" style={{ 
                  fontSize: '12px', 
                  fontWeight: '500', 
                  color: '#495057', 
                  marginBottom: '4px',
                  display: 'block'
                }}>
                  Status
                </label>
                <Select
                  value={statusOptions.find(option => option.value === statusFilter)}
                  onChange={handleStatusFilterChange}
                  options={statusOptions}
                  placeholder="Status"
                  className="filter-select"
                  classNamePrefix="select"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minWidth: '150px',
                      height: '36px',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      boxShadow: 'none'
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: '36px',
                      padding: '0 8px'
                    }),
                    input: (provided) => ({
                      ...provided,
                      margin: '0px'
                    }),
                    indicatorSeparator: () => ({
                      display: 'none'
                    }),
                    indicatorsContainer: (provided) => ({
                      ...provided,
                      height: '36px'
                    })
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Players Table with Shimmer Loader */}
      {loading || isPageLoading || isLoadingAll ? (
        <Card>
          <Card.Body>
            <ShimmerLoader type="table" rows={5} />
          </Card.Body>
        </Card>
      ) : (
        <PlayerTable
          players={filteredPlayers}
          loading={false}
          showView={true}
        showEdit={canManagePlayers}
        showDelete={canManagePlayers}
          showPaymentColumns={false}
          showQrActions={PLAYER_ASSETS_ENABLED}
          showPlayerIdCardActions={PLAYER_ASSETS_ENABLED}
          assetColumnMode="separate"
          allowActionsForApprovedPlayers={true}
          onAssetView={handleAssetView}
          onAssetDownload={handleAssetDownload}
          onView={handleViewPlayer}
          onEdit={handleEditPlayer}
          onDelete={handleDeletePlayer}
          title="Player Lineups"
          emptyMessage="No players found"
          emptySubMessage="No players available in the system."
        />
      )}

      {/* Show loading status when fetching all players */}
      {isLoadingAll && (
        <div className="pagination-section mt-4" style={{ 
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="page-info">
            <span className="text-muted" style={{ 
              fontSize: '14px', 
              fontWeight: '500'
            }}>
              Loading all {totalCount || '...'} players...
            </span>
          </div>
        </div>
      )}

      {/* Show info when all players are loaded */}
      {!isLoadingAll && allPlayersLoaded && totalCount > 0 && (
        <div className="pagination-section mt-4" style={{ 
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div className="page-info">
            <span className="text-muted" style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#6c757d'
            }}>
              Showing all {totalCount} players
            </span>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDelete}
        title="Delete Player"
        message={`Are you sure you want to delete ${playerToDelete?.fullName}? This action cannot be undone.`}
        isLoading={isDeleting}
        error={deleteError}
      />
      {PLAYER_ASSETS_ENABLED && (
        <PlayerAssetPreviewModal
          show={!!assetPreviewConfig}
          assetType={assetPreviewConfig?.type || 'qrCode'}
          assetUrl={
            assetPreviewConfig
              ? assetPreviewConfig.type === 'qrCode'
                ? assetPreviewConfig.player.qrCodeUrl
                : assetPreviewConfig.player.playerIdCardUrl
              : undefined
          }
          playerName={assetPreviewConfig?.player.fullName}
          onClose={() => setAssetPreviewConfig(null)}
          onDownload={() => {
            if (!assetPreviewConfig) return;
            handleAssetDownload(assetPreviewConfig.player, assetPreviewConfig.type);
          }}
        />
      )}
      </Container>
    </div>
  );
};

export default ManagePlayers; 